import * as React from 'react';

import SlFormatNumber from '@shoelace-style/shoelace/dist/react/format-number';
import { useAtomValue } from 'jotai';

import { sendPassRateEvent } from '~analytics/index';
import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms';
import { UIButton } from '~shared/components/buttons';
import { Popper } from '~shared/components/Popper';

import { PassThresholdPopoverContent } from './PassThresholdPopoverContent';

interface Props {
  activityInstanceId: number;
  passRate: number | null;
  totalStudents?: number | null;
  presentStudents?: number;
  completionRate?: number;
  averageScoreRate?: number | null;
  passingStudentsRate?: number | null;
}

function getParticipatingStudentsText(
  presentStudents: number,
  totalStudents?: number | null
): string {
  if (totalStudents) {
    return translate('{0} of {1} students').format(
      presentStudents,
      totalStudents
    );
  }

  if (presentStudents === 1) {
    return translate('1 student');
  }

  return translate('{0} students').format(presentStudents);
}

export function ResultsStatsRow({
  activityInstanceId,
  passRate,
  totalStudents,
  presentStudents,
  completionRate,
  averageScoreRate,
  passingStudentsRate,
}: Props) {
  const locale = useAtomValue(languageAtom);
  const colClassNames = 'flex flex-col justify-center p-4 border-0';
  const isPassRateSet = passRate !== null && passRate > 0;

  const participatingStudentsText = getParticipatingStudentsText(
    presentStudents ?? 0,
    totalStudents
  );

  // Use live score if available, otherwise fall back to prop
  const displayAverageScore = averageScoreRate ?? 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 rounded-2 overflow-hidden border border-solid border-grey-200 text-center text-blue-900 text-base leading-8 font-400 divide-y sm:divide-y-0 sm:divide-x divide-solid divide-grey-200">
      <div className="bg-grey-50 font-600 p-4 grid place-items-center">
        {participatingStudentsText}
      </div>
      <div className={colClassNames}>
        <p>{translate('Completion %')}</p>
        <SlFormatNumber
          value={(completionRate ?? 0) / 100}
          type="percent"
          lang={locale}
          maximumSignificantDigits={4}
        />
      </div>
      <div className={colClassNames}>
        <p>{translate('Avg. Score')}</p>
        <SlFormatNumber
          value={displayAverageScore / 100}
          type="percent"
          lang={locale}
          maximumSignificantDigits={4}
        />
      </div>
      <div className={colClassNames}>
        <p>{translate('Pass Rate')}</p>
        <div className="flex items-center justify-center">
          {isPassRateSet ? (
            <span className="font-600">
              {
                <SlFormatNumber
                  value={(passingStudentsRate ?? 0) / 100}
                  type="percent"
                  lang={locale}
                  maximumSignificantDigits={4}
                />
              }{' '}
              (
            </span>
          ) : null}
          <Popper
            theme={'light'}
            mode={'click'}
            enableArrow={false}
            content={(closePopper) => (
              <div
                style={{
                  maxWidth: '320px',
                  boxShadow: '0px 16px 72px 0px #00000029',
                }}
                className={'rounded-4 bg-white p-6'}
              >
                <PassThresholdPopoverContent
                  onClose={closePopper}
                  currentThreshold={passRate}
                  activityInstanceId={activityInstanceId}
                />
              </div>
            )}
          >
            <UIButton
              appearance="text"
              className="text-cyan-600 font-600"
              style={{ padding: 0 }}
              onClick={() => {
                sendPassRateEvent({
                  action: isPassRateSet ? 'graded' : 'set',
                });
              }}
            >
              <div className="font-600 font-sans text-base">
                {isPassRateSet ? (
                  <>
                    ≥
                    <SlFormatNumber
                      value={(passRate ?? 0) / 100}
                      type="percent"
                      lang={locale}
                      maximumSignificantDigits={4}
                    />
                  </>
                ) : (
                  translate('Set')
                )}
              </div>
            </UIButton>
          </Popper>
          {isPassRateSet ? <span className="font-600">)</span> : null}
        </div>
      </div>
    </div>
  );
}
